import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { BranchOffice, User } from '@/services/userService';
import { Loader2, Plus, Edit, MapPin, Phone, Mail, Building2, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useBranchOffices, useBranchOfficeMutations, useUsers } from '@/hooks/useProfiles';

interface BranchFormData {
  branch_name: string;
  branch_code?: string;
  is_headquarters: boolean;
  street: string;
  suite?: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  fax?: string;
  email?: string;
  branch_manager_id?: string;
  is_active: boolean;
}

export const BranchOfficeManagement: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin') || false;

  // Use hooks for data fetching
  const { branches = [], loading: branchesLoading, error: branchesError, refetch: refetchBranches } = useBranchOffices();
  const { users: loanOfficers = [], loading: officersLoading } = useUsers();
  const { createBranch, updateBranch, _deleteBranch, _updating } = useBranchOfficeMutations();
  
  const [selectedBranch, setSelectedBranch] = useState<BranchOffice | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<BranchFormData>();

  const loading = branchesLoading || officersLoading;

  // Show error if data fails to load
  useEffect(() => {
    if (branchesError) {
      toast.error(branchesError);
    }
  }, [branchesError]);

  const openCreateDialog = () => {
    setIsEditMode(false);
    setSelectedBranch(null);
    reset({
      branch_name: '',
      branch_code: '',
      is_headquarters: false,
      street: '',
      suite: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      fax: '',
      email: '',
      branch_manager_id: '',
      is_active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (branch: BranchOffice) => {
    setIsEditMode(true);
    setSelectedBranch(branch);
    reset({
      branch_name: branch.branch_name,
      branch_code: branch.branch_code || '',
      is_headquarters: branch.is_headquarters,
      street: branch.address.street,
      suite: branch.address.suite || '',
      city: branch.address.city,
      state: branch.address.state,
      zip: branch.address.zip,
      phone: branch.phone || '',
      fax: branch.fax || '',
      email: branch.email || '',
      branch_manager_id: branch.branch_manager_id || '',
      is_active: branch.is_active
    });
    setIsDialogOpen(true);
  };

  const onSubmit = async (data: BranchFormData) => {
    try {
      const branchData = {
        branch_name: data.branch_name,
        branch_code: data.branch_code,
        is_headquarters: data.is_headquarters,
        address: {
          street: data.street,
          suite: data.suite,
          city: data.city,
          state: data.state,
          zip: data.zip
        },
        phone: data.phone,
        fax: data.fax,
        email: data.email,
        branch_manager_id: data.branch_manager_id || undefined,
        is_active: data.is_active,
        organization_id: '', // This will be set by the backend
        company_profile_id: '' // This will be set by the backend
      };

      let success = false;
      if (isEditMode && selectedBranch) {
        const result = await updateBranch(selectedBranch.id, branchData);
        success = result !== null;
      } else {
        const result = await createBranch(branchData);
        success = result !== null;
      }

      if (success) {
        setIsDialogOpen(false);
        refetchBranches();
      }
    } catch (error) {
      console.error('Error saving branch office:', error);
      toast.error('Failed to save branch office');
    }
  };

  const getManagerName = (managerId?: string) => {
    if (!managerId || !loanOfficers || loanOfficers.length === 0) {
      return null;
    }
    const manager = loanOfficers.find(lo => lo.id === managerId);
    return manager?.display_name || 'Unknown';
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Admin Access Required</h3>
              <p className="text-muted-foreground">
                Only administrators can manage branch offices.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Branch Offices</h1>
        <p className="text-muted-foreground">Manage your company's branch locations</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle>All Branches</CardTitle>
              <CardDescription>
                {branches?.length || 0} branch office{(branches?.length || 0) !== 1 ? 's' : ''} configured
              </CardDescription>
            </div>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Branch Office
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!branches || branches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No branch offices configured
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => (
                  <TableRow key={branch.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{branch.branch_name}</div>
                          {branch.branch_code && (
                            <div className="text-sm text-muted-foreground">Code: {branch.branch_code}</div>
                          )}
                        </div>
                        {branch.is_headquarters && (
                          <Badge variant="secondary" className="ml-2">HQ</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {branch.address.street}
                          {branch.address.suite && `, ${branch.address.suite}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {branch.address.city}, {branch.address.state} {branch.address.zip}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {branch.phone && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3 text-muted-foreground" />
                            {branch.phone}
                          </div>
                        )}
                        {branch.email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            {branch.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getManagerName(branch.branch_manager_id) || (
                        <span className="text-muted-foreground">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={branch.is_active ? "default" : "secondary"}
                        className={branch.is_active ? "bg-green-500 hover:bg-green-600" : ""}
                      >
                        {branch.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(branch)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Branch Office</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update the branch office information' : 'Add a new branch office to your organization'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="branch_name">Branch Name</Label>
                <Input
                  id="branch_name"
                  {...register('branch_name', { required: 'Branch name is required' })}
                  placeholder="Downtown Branch"
                />
                {errors.branch_name && (
                  <p className="text-sm text-red-600">{errors.branch_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch_code">Branch Code (Optional)</Label>
                <Input
                  id="branch_code"
                  {...register('branch_code')}
                  placeholder="BR001"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_headquarters"
                checked={watch('is_headquarters')}
                onCheckedChange={(checked) => setValue('is_headquarters', checked)}
              />
              <Label htmlFor="is_headquarters">
                This is the headquarters location
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Branch Address</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  {...register('street', { required: 'Street is required' })}
                  placeholder="Street Address"
                />
                <Input
                  {...register('suite')}
                  placeholder="Suite/Unit (optional)"
                />
                <Input
                  {...register('city', { required: 'City is required' })}
                  placeholder="City"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    {...register('state', { required: 'State is required' })}
                    placeholder="State"
                    maxLength={2}
                  />
                  <Input
                    {...register('zip', { required: 'ZIP is required' })}
                    placeholder="ZIP Code"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fax">Fax</Label>
                <Input
                  id="fax"
                  {...register('fax')}
                  placeholder="(555) 123-4568"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                placeholder="branch@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="branch_manager_id">Branch Manager</Label>
              <Select
                value={watch('branch_manager_id') || 'unassigned'}
                onValueChange={(value) => setValue('branch_manager_id', value === 'unassigned' ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a branch manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">No manager assigned</SelectItem>
                  {loanOfficers?.map((officer) => (
                    <SelectItem key={officer.id} value={officer.id}>
                      {officer.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={watch('is_active')}
                onCheckedChange={(checked) => setValue('is_active', checked)}
              />
              <Label htmlFor="is_active">
                Branch is active
              </Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditMode ? 'Update' : 'Create'} Branch
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};